import type { Photo } from '../content/images.ts';
import { SHOW_EMPTY_IMAGE_SLOTS } from '../content/images.ts';
import { Icon } from '@shared/components/Icon.tsx';

type Ratio = '16/9' | '4/3' | '1/1' | '3/4';

/**
 * One image slot. Renders the photo if configured; otherwise nothing — or, in
 * the demo build, a dashed placeholder showing where a photo will go.
 */
export function ImageSlot({
  photo,
  ratio = '4/3',
  label,
  className = '',
  eager = false,
}: {
  photo: Photo | null | undefined;
  ratio?: Ratio;
  /** Shown in the empty placeholder, e.g. "मुखपृष्ठ छायाचित्र" */
  label: string;
  className?: string;
  eager?: boolean;
}) {
  if (!photo) {
    if (!SHOW_EMPTY_IMAGE_SLOTS) return null;
    return (
      <div className={`img-slot img-slot--empty ${className}`} style={{ aspectRatio: ratio }} aria-hidden='true'>
        <Icon name='image' size={30} />
        <span>{label}</span>
        <small>content/images.ts मध्ये छायाचित्र जोडा</small>
      </div>
    );
  }
  return (
    <figure className={`img-slot ${className}`}>
      <img
        src={photo.src}
        alt={photo.alt}
        loading={eager ? 'eager' : 'lazy'}
        decoding='async'
        style={{ aspectRatio: ratio, objectPosition: photo.objectPosition }}
      />
      {photo.caption && <figcaption>{photo.caption}</figcaption>}
    </figure>
  );
}

/** Responsive photo gallery: grid on laptops, swipeable strip on phones. Hidden when empty. */
export function Gallery({ photos, title, emptyCount = 3 }: { photos: Photo[]; title: string; emptyCount?: number }) {
  if (!photos.length && !SHOW_EMPTY_IMAGE_SLOTS) return null;
  return (
    <section className='section'>
      <h2 className='section-title'>{title}</h2>
      <div className='gallery'>
        {photos.length
          ? photos.map((p) => <ImageSlot key={p.src} photo={p} label='' />)
          : Array.from({ length: emptyCount }, (_, i) => <ImageSlot key={i} photo={null} label={`छायाचित्र ${i + 1}`} />)}
      </div>
    </section>
  );
}
